import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  AddButton,
  BackButton,
  ThinDropdown,
  ThinTextField,
} from "../../common/GeneralComponent";
import { fetchData } from "../../../utils/functions";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import Loader from "../../../components/partials/Loader";

const EditCycle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loader, setLoader] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cycle, setCycle] = useState({
    cycle_name: "",
    status: 1,
  });

  const validationSchema = Yup.object().shape({
    cycle_name: Yup.string()
      .trim()
      .min(3, "Cycle name must be at least 3 characters long")
      .max(100, "Cycle name must not exceed 100 characters")
      .required("Cycle name is required"),
    status: Yup.number()
      .oneOf([0, 1], "Status must be Active or In-Active")
      .required("Status is required"),
  });

  // Fetch cycle data
  useEffect(() => {
    const fetchCycleData = async () => {
      try {
        const response = await fetchData(
          `cycle/${id}`,
          "GET",
          null,
          {},
          {},
          navigate,
          "/cycles",
          dispatch
        );

        const data = response?.data;
        if (data) {
          setCycle({
            cycle_name: data?.cycle_name || "",
            status: data?.status ?? 0,
          });
          setLoader(false);
        }
      } catch (error) {
        setLoader(false);
        console.error("Error fetching cycle data:", error);
        toast.error("Failed to load cycle data");
      }
    };

    fetchCycleData();
  }, [id, navigate, dispatch]);

  const formik = useFormik({
    initialValues: cycle,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);

        const data = {
          cycle_name: values.cycle_name,
          status: values.status,
        };

        const response = await fetchData(
          `cycle`,
          "PUT",
          { ...data, id },
          {},
          {},
          navigate,
          "/cycles",
          dispatch
        );
        toast.success(response?.message || "Cycle updated successfully");
        navigate("/cycles");
      } catch (error) {
        console.error("Error updating cycle:", error);
        toast.error(error?.message || "Failed to update cycle");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <div className="w-full my-4 mx-auto">
      {loader && <Loader />}

      <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Edit Cycle
        </h1>
        <BackButton text="Back" to="/cycles" />
      </div>

      <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4 mb-[7rem]">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b ">
          <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Edit Cycle
          </h1>
        </div>
        <form className="p-4" onSubmit={formik.handleSubmit}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ThinTextField
              id="cycle_name"
              label="Cycle Name"
              placeholder="e.g., Cycle 1, Q1 2024"
              type="text"
              name="cycle_name"
              value={formik.values.cycle_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.cycle_name &&
                Boolean(formik.errors.cycle_name)
              }
              helperText={
                formik.touched.cycle_name && formik.errors.cycle_name
              }
            />

            <ThinDropdown
              label="Status"
              id="status-dropdown"
              name="status"
              options={[
                { value: 1, label: "Active" },
                { value: 0, label: "In-Active" },
              ]}
              value={formik.values.status}
              onChange={(e) =>
                formik.setFieldValue("status", Number(e.target.value))
              }
              onBlur={() => formik.setFieldTouched("status", true)}
              error={formik.touched.status && formik.errors.status}
              helperText={formik.touched.status && formik.errors.status}
            />
          </div>
          <div className="p-4">
            <AddButton type="Submit" text="Update" isLoading={isSubmitting} />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCycle;
